import { useEffect, useMemo, type ReactNode } from "react";
import { CircleMarker, MapContainer, Polyline, TileLayer, Tooltip, useMap } from "react-leaflet";
import type { LatLngBoundsExpression, LatLngExpression } from "leaflet";
import { CONDITION_COLOR } from "./Badges";
import { COLORS } from "../styles/themeColors";

export interface MapLine {
  id: string;
  points: [number, number][];
  color?: string;
  label?: string;
  weight?: number;
}

export interface MapPin {
  id: string;
  lat: number;
  lon: number;
  color: string;
  label: string;
  dashed?: boolean;
}

const KENYA: LatLngBoundsExpression = [[-4.7, 33.9], [5.0, 41.9]];

function Fit({ bounds }: { bounds: LatLngBoundsExpression | null }) {
  const map = useMap();
  useEffect(() => {
    if (bounds) map.fitBounds(bounds, { padding: [24, 24], maxZoom: 16 });
  }, [map, bounds]);
  return null;
}

function Focus({ point }: { point: LatLngExpression | null }) {
  const map = useMap();
  useEffect(() => {
    if (point) map.panTo(point, { animate: true });
  }, [map, point]);
  return null;
}

export function MapPanel({
  lines = [], pins = [], selectedId, onSelectLine, onSelectPin, height = 420, label, overlay, overlayTopRight, tiles = "light",
}: {
  lines?: MapLine[];
  pins?: MapPin[];
  selectedId?: string | null;
  onSelectLine?: (id: string) => void;
  onSelectPin?: (id: string) => void;
  height?: number;
  label: string;
  /** Frosted panel floating bottom-left over the map (legend, key facts). */
  overlay?: ReactNode;
  overlayTopRight?: ReactNode;
  tiles?: "light" | "dark";
}) {
  const bounds = useMemo<LatLngBoundsExpression | null>(() => {
    const all = lines.flatMap((l) => l.points);
    if (!all.length) return null;
    const lats = all.map((p) => p[0]);
    const lons = all.map((p) => p[1]);
    return [[Math.min(...lats), Math.min(...lons)], [Math.max(...lats), Math.max(...lons)]];
  }, [lines]);

  const selected = pins.find((p) => p.id === selectedId);

  return (
    <div className={`map${tiles === "dark" ? " dark" : ""}`} style={{ "--map-h": `${height}px` } as React.CSSProperties} role="region" aria-label={label}>
      <MapContainer bounds={bounds ?? KENYA} scrollWheelZoom={false} preferCanvas>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Fit bounds={bounds} />
        <Focus point={selected ? [selected.lat, selected.lon] : null} />
        {lines.map((l) => (
          <Polyline
            key={l.id}
            positions={l.points}
            pathOptions={{ color: l.color ?? CONDITION_COLOR["Not rated"], weight: l.weight ?? 5, opacity: 0.9 }}
            eventHandlers={onSelectLine ? { click: () => onSelectLine(l.id) } : undefined}
          >
            {l.label ? <Tooltip sticky>{l.label}</Tooltip> : null}
          </Polyline>
        ))}
        {pins.map((p) => {
          const isSel = p.id === selectedId;
          return (
            <CircleMarker
              key={p.id}
              center={[p.lat, p.lon]}
              radius={isSel ? 9 : 6}
              pathOptions={{
                color: isSel ? COLORS.navyInk : p.color,
                weight: isSel ? 3 : 2,
                fillColor: p.color,
                fillOpacity: p.dashed ? 0.25 : 0.9,
                dashArray: p.dashed ? "3 3" : undefined,
              }}
              eventHandlers={onSelectPin ? { click: () => onSelectPin(p.id) } : undefined}
            >
              <Tooltip>{p.label}</Tooltip>
            </CircleMarker>
          );
        })}
      </MapContainer>
      {overlay ? <div className="map-overlay">{overlay}</div> : null}
      {overlayTopRight ? <div className="map-overlay top-right">{overlayTopRight}</div> : null}
    </div>
  );
}

export function ConditionLegend() {
  return (
    <div className="legend" aria-label="Map legend">
      {Object.entries(CONDITION_COLOR).map(([k, c]) => (
        <span key={k}><i style={{ background: c }} />{k}</span>
      ))}
    </div>
  );
}
